import React, { useCallback, useState } from 'react';
import { styled } from '@mui/material/styles';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { usePageSize } from '@hooks/usePageSize';
import { useAuth } from '@contexts/AuthContext';
import { useProId } from '@contexts/ProIdContext';
import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import AutoCompletePro from '@components/AutoComplete/AutoCompletePro';
import { ADD_JOB_OFFER_TEMPLATE, ADD_JOB_OFFER_TEMPLATE_UPERCUT, GET_JOB_OFFER_TEMPLATES } from '@gql/jobOfferTemplates';
import { useGetJobOfferTemplates } from './hooks/useGetJobOfferTemplates';
import JobOfferTemplateDataTable from './JobOfferTemplateDataTable';
import { JobOfferTemplateFilter, IJobOfferTemplateFilter, defaultTemplateFilters } from './JobOfferTemplateFilter';

const ContainerWrapper = styled(Box)(({ theme }) => ({
    borderRadius: '24px',
    background: 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    flex: 1,
}));

interface JobOfferTemplateManagementProps {
    proId?: string;
}

const JobOfferTemplateManagement: React.FC<JobOfferTemplateManagementProps> = ({ proId: lockedProId }) => {
    const navigate = useNavigate();
    const { isUpercut } = useAuth();
    const { selectedProId } = useProId();
    const { showSnackbar } = useSnackbar();

    const [query, setQuery] = useState('');
    const [filter, setFilter] = useState<IJobOfferTemplateFilter>({
        ...defaultTemplateFilters,
        ...(lockedProId ? { proId: [lockedProId] } : {}),
    });
    const { pageSize, storePageSize } = usePageSize({ storageKey: 'job-offer-templates-page-size' });
    const [pagination, setPagination] = useState({ page: 0, pageSize });

    // Dialog state
    const [dialogOpen, setDialogOpen] = useState(false);
    const [newTitle, setNewTitle] = useState('');
    const [dialogProId, setDialogProId] = useState<string | null>(null);

    const onQueryChange = useCallback((value: string) => {
        setQuery(value);
        setPagination(prev => ({ ...prev, page: 0 }));
    }, []);

    const onFilterChange = useCallback((newFilter: IJobOfferTemplateFilter) => {
        setFilter(newFilter);
        setPagination(prev => ({ ...prev, page: 0 }));
    }, []);

    const { templates, totalDocs, loading, refetch } = useGetJobOfferTemplates(
        query,
        filter,
        'createdDate',
        pagination.page,
        pagination.pageSize,
    );

    const mutationDoc = isUpercut ? ADD_JOB_OFFER_TEMPLATE_UPERCUT : ADD_JOB_OFFER_TEMPLATE;
    const resultKey = isUpercut ? 'addJobOfferTemplateUpercut' : 'addJobOfferTemplatePro';

    const [addTemplate, { loading: creating }] = useMutation(mutationDoc, {
        refetchQueries: [GET_JOB_OFFER_TEMPLATES],
        onCompleted: (result) => {
            const template = result?.[resultKey];
            handleCloseDialog();
            if (template?.jobNumber) {
                navigate(`/job-offer-templates/${template.jobNumber}/detail`);
            }
        },
        onError: (error) => {
            showSnackbar(error.message, 'error');
        },
    });

    const handleOpenDialog = () => {
        setDialogOpen(true);
        setNewTitle('');
        setDialogProId(null);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setNewTitle('');
        setDialogProId(null);
    };

    const handleCreate = async () => {
        if (!newTitle.trim()) return;
        await addTemplate({
            variables: {
                templateInput: {
                    title: newTitle.trim(),
                    status: 'DRAFT',
                },
                proId: lockedProId || dialogProId || selectedProId || undefined,
            },
        });
    };

    const canCreate = !!newTitle.trim() && (!isUpercut || !!lockedProId || !!dialogProId || !!selectedProId);

    return (
        <ContainerWrapper>
            <JobOfferTemplateFilter
                totalDocs={totalDocs}
                query={query}
                onQueryChange={onQueryChange}
                onFilterChange={onFilterChange}
                onAddTemplate={handleOpenDialog}
                filter={filter}
                lockedProId={lockedProId}
            />

            <JobOfferTemplateDataTable
                data={templates}
                loading={loading}
                currentPage={pagination.page + 1}
                pageSize={pagination.pageSize}
                totalRows={totalDocs}
                onPageChange={page => setPagination(prev => ({ ...prev, page: page - 1 }))}
                onPageSizeChange={limit => {
                    storePageSize(limit);
                    setPagination({ page: 0, pageSize: limit });
                }}
                onDeleted={refetch}
            />

            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ pb: 1 }}>
                    <Typography variant="h6" fontWeight={600}>
                        Créer un modèle d'offre d'emploi
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                        {!lockedProId && (
                            <AutoCompletePro
                                size="small"
                                required
                                onChange={(proId) => setDialogProId(proId)}
                            />
                        )}
                        <TextField
                            label="Titre du modèle"
                            value={newTitle}
                            onChange={e => setNewTitle(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && canCreate && !creating && handleCreate()}
                            size="small"
                            fullWidth
                            placeholder="Ex: Coiffeur CDI Paris"
                            inputProps={{ maxLength: 200 }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button onClick={handleCloseDialog} disabled={creating} variant="text" color="secondary" sx={{ textTransform: 'none' }}>
                        Annuler
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleCreate}
                        disabled={!canCreate || creating}
                        startIcon={creating ? <CircularProgress size={14} color="inherit" /> : undefined}
                        sx={{ textTransform: 'none', borderRadius: '10px' }}
                    >
                        {creating ? 'Création…' : 'Suivant'}
                    </Button>
                </DialogActions>
            </Dialog>
        </ContainerWrapper>
    );
};

export default JobOfferTemplateManagement;
